// ─────────────────────────────────────────────────────────────────────────────
// CompanyProfile tab — the only tab backed by a real save endpoint.
// Data + save live in useCompanySettings; this component registers that save fn
// on `saveRef` so the header's Save Changes button can invoke it.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import styles from "./panes.module.css";
import { useCompanySettings } from "../hooks/useCompanySettings";
import { COMPANY_SIZES, INDUSTRIES } from "../../../services/settingsService";
import {
  SectionTitle, Divider, Field, FieldList, Input, Select,
  LoadingPane, ErrorBanner,
} from "./fields";

export default function CompanyProfile({ saveRef, readOnly = false }) {
  const { form, loading, error, setField, save } = useCompanySettings();

  // Hand the save fn up to the page (the Save button lives in the header).
  useEffect(() => {
    if (saveRef) saveRef.current = save;
  }, [save, saveRef]);

  if (loading)        return <LoadingPane/>;
  if (!form)          return <ErrorBanner>{error || "Failed to load settings."}</ErrorBanner>;

  return (
    <div className={styles.pane}>
      {error && <ErrorBanner>{error}</ErrorBanner>}

      <SectionTitle>Company Profile</SectionTitle>
      <FieldList>
        <Field label="Workspace Name">
          <Input value={form.workspaceName} onChange={setField("workspaceName")}
            placeholder="My Organisation" readOnly={readOnly}/>
        </Field>
        <Field label="Admin Email" hint="Read-only — this is your login email">
          <Input value={form.adminEmail} readOnly/>
        </Field>
        <Field label="Primary Contact Email">
          <Input value={form.primaryContactEmail} onChange={setField("primaryContactEmail")}
            placeholder="contact@company.com" type="email" readOnly={readOnly}/>
        </Field>
        <Field label="Industry">
          <Select value={form.industry} onChange={setField("industry")} options={INDUSTRIES} disabled={readOnly}/>
        </Field>
        <Field label="Company Size">
          <Select value={form.companySize} onChange={setField("companySize")} options={COMPANY_SIZES} disabled={readOnly}/>
        </Field>
      </FieldList>

      <Divider/>

      <SectionTitle>Data Retention</SectionTitle>
      <FieldList>
        <Field label="Audit Log Retention (Days)" hint="Leave empty to keep logs forever">
          <Input value={form.auditLogRetentionDays} onChange={setField("auditLogRetentionDays")}
            placeholder="e.g. 90" type="number" readOnly={readOnly}/>
        </Field>
      </FieldList>
    </div>
  );
}
